/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useRequest } from '../../hooks/UseRequest'
import { useDispatch, useSelector } from 'react-redux'
import Loading from '../../components/Loading'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faTimesCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { faClock } from '@fortawesome/free-regular-svg-icons'
import { setModal } from '../../redux/modalSlice'
import { selectUser } from '../../redux/userSlice'
import { hasUserPermission } from '../../utils/permissionHelper'
import { selectSubpanelRerender } from '../../redux/rerenderSlice'
import { DateFormat } from '../../helpers/formatting'

export default function RewardPointDashlet({ endpointPath, customerId }) {
  const dispatch = useDispatch()
  const requests = useRequest()
  const requesting = useRef(false)
  const [row, setRow] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)
  const active = useSelector(selectUser)
  const activePermissions = active.spoofed_user.user_permission_options ? active.spoofed_user.user_permission_options : active.data.user_permission_options
  const rerenderValue = useSelector((state) => selectSubpanelRerender(state, 'reward_point_dashlet'))

  const getRow = async () => {
    requests.setEndpoint(endpointPath)
    try {
      const response = await requests.get()
      setRow(response.data.data)
      setError(false)
      requesting.current = false
      setLoaded(true)
    } catch (err) {
      setError(true)
      requesting.current = false
      setLoaded(true)
    }
  }

  useEffect(() => {
    if (!requesting.current && endpointPath && (row.length === 0 || rerenderValue)) {
      setLoaded(false)
      requesting.current = true
      getRow()
    }
  }, [endpointPath, rerenderValue])

  return (
    <div className="message is-white lox-dashlet-detail">
      <div className="message-header has-background-primary has-text-white lox-dashlet-detail__title py-0">
        <p className="is-size-4">Reward Points Detail</p>
        <p className="is-size-6">
          <span
            className="is-clickable"
            data-testid="reward-dashlet-view-audit"
            onClick={() => {
              dispatch(setModal({ is_active: true, type: 'reward_audit_log', options: { customerId } }))
            }}
          >
            <FontAwesomeIcon icon={faClock} className="mr-1" /> View Audit
          </span>
        </p>
      </div>
      <div className="message-body py-0 px-1" style={{ lineHeight: 1.40 }}>
        {loaded && !error && row && (
          <>
            {row.loan_id && (
              <>
                <div className="lox-dashlet-detail__item py-2 px-2 is-flex is-justify-content-space-between is-align-items-center">
                  <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                    Loan
                  </p>
                  <p className="lox-dashlet-detail__item__data has-text-link is-size-8">
                    <Link to={`/loan/${row.loan_id}`} className="lox-has-text-decoration-none">
                      Loan #{row.loan_id}
                    </Link>
                  </p>
                </div>
                <div className="divider my-1 mx-1"></div>
              </>
            )}

            <div className="lox-dashlet-detail__item py-2 px-2 is-flex is-justify-content-space-between is-align-items-center">
              <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                Total Earned
              </p>
              <p className="lox-dashlet-detail__item__data has-text-dark is-size-8" data-testid="reward-dashlet-total-earned">
                {row.total_earned ?? 0} pts
              </p>
            </div>
            <div className="divider my-1 mx-1"></div>

            <div className="lox-dashlet-detail__item py-2 px-2 is-flex is-justify-content-space-between is-align-items-center">
              <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                Redeemed
              </p>
              <p className="lox-dashlet-detail__item__data has-text-dark is-size-8" data-testid="reward-dashlet-redeemed">
                {row.redeemed ?? 0} pts
              </p>
            </div>
            <div className="divider my-1 mx-1"></div>

            <div className="lox-dashlet-detail__item py-2 px-2 is-flex is-justify-content-space-between is-align-items-center">
              <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                Available
              </p>
              <p className="lox-dashlet-detail__item__data has-text-dark is-size-8 has-text-weight-bold" data-testid="reward-dashlet-available">
                {row.available ?? 0} pts
              </p>
            </div>

            {hasUserPermission(activePermissions, 'customer_reward_redeem') && row.is_eligible && row.available > 0 && (
              <div className="lox-dashlet-detail__item py-2 px-2 is-flex is-justify-content-space-between is-align-items-center">
                <p className="lox-dashlet-detail__item__label has-text-grey is-italic is-size-8">
                  Apply toward the loan balance
                </p>
                <button
                  type="button"
                  className="button is-link is-small"
                  data-testid="reward-dashlet-redeem-button"
                  onClick={() => {
                    dispatch(setModal({
                      is_active: true,
                      type: 'redeem_for_paydown_modal',
                      options: { customerId, available: row.available, loanId: row.loan_id }
                    }))
                  }}
                >
                  Redeem for Paydown
                </button>
              </div>
            )}
            <div className="divider my-1 mx-1"></div>

            <div className="lox-dashlet-detail__item py-2 px-2 is-flex is-justify-content-space-between is-align-items-center">
              <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                Redemption Status
              </p>
              <p className="lox-dashlet-detail__item__data is-size-8" data-testid="reward-dashlet-status">
                {row.is_eligible ? (
                  <span className="tag is-success is-rounded">
                    <FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Eligible to Redeem
                  </span>
                ) : (
                  <span className="tag is-danger is-rounded">
                    <FontAwesomeIcon icon={faTimesCircle} className="mr-1" /> Not Eligible to Redeem
                  </span>
                )}
              </p>
            </div>

            {!row.is_eligible && row.ineligible_reasons?.length > 0 && (
              <>
                <div className="divider my-1 mx-1"></div>
                <div className="py-2 px-2" data-testid="reward-dashlet-ineligible-reasons">
                  <p className="has-text-weight-bold is-size-8 mb-1">WHY CAN&apos;T THEY REDEEM?</p>
                  <ul>
                    {row.ineligible_reasons.map((reason) => (
                      <li key={reason} className="is-size-8 has-text-grey-dark py-1">
                        <FontAwesomeIcon icon={faInfoCircle} className="has-text-danger mr-2" />
                        {reason}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
            <div className="divider my-1 mx-1"></div>

            <div className="lox-dashlet-detail__item py-2 px-2 is-flex is-justify-content-space-between is-align-items-center">
              <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                Unclaimed
              </p>
              <p className="lox-dashlet-detail__item__data has-text-dark is-size-8" data-testid="reward-dashlet-unclaimed">
                {row.unclaimed ?? 0} pts
              </p>
            </div>
            <p className="has-text-grey is-italic is-size-8 px-2 pb-2">
              Requires the customer to accept — not agent-initiated
            </p>

            {row.expiring_soon_points > 0 && (
              <div className="notification is-warning is-light py-2 px-2 mx-2 mb-2 is-size-8" data-testid="reward-dashlet-expiring-soon">
                <FontAwesomeIcon icon={faClock} className="mr-2" />
                <span className="has-text-weight-bold">{row.expiring_soon_points} of {row.unclaimed ?? 0} unclaimed pts</span> expire within 60 days — starting on <DateFormat dateString={row.expiring_soon_by_date} />
              </div>
            )}
          </>
        )}
        {!loaded && <Loading />}
        {loaded && error && (
          <div className="is-size-6 is-italic" style={{ textAlign: "center", padding: "20px", color: "grey" }}>Unable to load reward points</div>
        )}
      </div>
    </div>
  )
}
